import {drawnClientTemplates, knownTemplates, type templates} from "data/templates";
import {findChannelObj} from "data/products";
import {getFilteredReleaseTemplates} from "utils/releases";
import {withoutHiddenTemplates} from "libs/bannerComposition";
import {withSlotTemplateIds, type TemplateSelection} from "data/templateSlots";

/**
 * The banners a route shows. The pages draw these, and the background selector
 * picks the banner for its crop previews from them, so the two cannot disagree.
 * Each list takes the page's template selection (data/templateSlots.ts): which
 * design of each slot is on air.
 */

type ClientTemplate = (typeof templates)[string][number];

/** Social layout 5 draws on these banners only. */
const SOCIAL_LAYOUT_5_TEMPLATE_IDS = [
  "Twitter",
  "BlogSocialShare",
  "BlogFeatured",
  "YouTubeCommunity",
  "SoMeVertical",
  "InstagramStories",
  "InstagramFeed",
];

export function getSocialTemplates(
  layoutOption: number | string,
  selection: TemplateSelection
): ClientTemplate[] {
  return drawnClientTemplates("social", selection).filter(
    (item) => Number(layoutOption) !== 5 || SOCIAL_LAYOUT_5_TEMPLATE_IDS.includes(item.id)
  );
}

/**
 * Email: the banners whose `layouts` in templateManifest.ts include the layout.
 * The Emails set of the Releases channel filters the same list the same way
 * (getFilteredReleaseTemplates).
 */
export function getEmailTemplates(
  layoutOption: number | string,
  selection: TemplateSelection
): ClientTemplate[] {
  return drawnClientTemplates("email", selection).filter((template) => {
    if (!template.layouts) throw new Error(`Email template "${template.id}" has no layouts`);
    return template.layouts.includes(Number(layoutOption));
  });
}

/**
 * Events: the banners the channel's `layoutsConfig` names for the layout, or
 * every banner when the channel has no `layoutsConfig`.
 */
export function getLayoutConfigTemplates(
  channel: "events",
  layoutOption: number | string,
  selection: TemplateSelection
): ClientTemplate[] {
  const layoutsConfig: Record<string, string[]> | undefined = findChannelObj(channel)?.layoutsConfig;
  return drawnClientTemplates(channel, selection).filter((template) => {
    if (!layoutsConfig) return true;
    return Boolean(layoutsConfig[String(layoutOption)]?.includes(template.id));
  });
}

/** Ads: the banners of the content type, on the layouts they declare. */
export function getAdsTemplates(
  type: string | null | undefined,
  layoutOption: number | string,
  selection: TemplateSelection
): ClientTemplate[] {
  // Templates declare which content types they belong to via `contentTypes:
  // [...]` in templateManifest.ts. Templates without that field show in every
  // content type (back-compat).
  return drawnClientTemplates("ads", selection).filter(
    (template) =>
      (!template.contentTypes || template.contentTypes.includes(type)) &&
      (!template.layouts || template.layouts.includes(Number(layoutOption)))
  );
}

/** Livestream: the "intro" Instagram Stories card is layout-6 only. */
export function getLivestreamTemplates(
  layoutOption: number | string,
  selection: TemplateSelection
): ClientTemplate[] {
  return drawnClientTemplates("livestream", selection).filter(
    (item) => !item.layouts || item.layouts.includes(Number(layoutOption))
  );
}

interface RouteTemplateOptions {
  layoutOption: number | string;
  set: string | null | undefined;
  product: string | null | undefined;
  /** The content type: Ads picks its banners by it. */
  type?: string | null;
  /** The banners an admin removed from the page (libs/bannerComposition.ts). */
  hiddenTemplates?: string[];
  /** Releases: the banners an admin added from other sets. */
  extraTemplates?: string[];
  /** Which design of each slot the page draws. */
  selection: TemplateSelection;
}

/**
 * The banners a page draws. A removed id (`hiddenTemplates`) names its place
 * on the page, so it also removes the design that replaced it.
 */
export function getRouteTemplates(
  channel: string,
  {hiddenTemplates = [], ...options}: RouteTemplateOptions
): ClientTemplate[] {
  return withoutHiddenTemplates(
    channelRouteTemplates(channel, options),
    withSlotTemplateIds(channel, hiddenTemplates)
  );
}

function channelRouteTemplates(
  channel: string,
  {layoutOption, set, product, type, extraTemplates = [], selection}: Omit<RouteTemplateOptions, "hiddenTemplates">
): ClientTemplate[] {
  switch (channel) {
    case "releases":
      return getFilteredReleaseTemplates({
        selectedSet: set,
        layoutOption,
        productValue: product,
        extraTemplateIds: extraTemplates,
        selection,
      });
    case "social":
      return getSocialTemplates(layoutOption, selection);
    case "email":
      return getEmailTemplates(layoutOption, selection);
    case "events":
      return getLayoutConfigTemplates(channel, layoutOption, selection);
    case "ads":
      return getAdsTemplates(type, layoutOption, selection);
    case "livestream":
      return getLivestreamTemplates(layoutOption, selection);
    default:
      // A channel without templates (the Grid, a stale url) draws none.
      return channel in knownTemplates ? drawnClientTemplates(channel, selection) : [];
  }
}
